const FORECAST_DECISION_TOLERANCE = 0.000001;

function isForecastRecommendationApplied(appliedDecisions, recommendationId, expectedNextPct) {
    const latest = appliedDecisions?.get(recommendationId);
    if (!latest?.isApproved) return false;
    const recordedNext = Number(latest.nextPct ?? latest.nextValue);
    if (!Number.isFinite(recordedNext)) return false;
    return Math.abs(recordedNext - expectedNextPct) < FORECAST_DECISION_TOLERANCE;
}

function isTopLineActionable(topLineRecommendation) {
    if (!topLineRecommendation) return false;
    return (
        topLineRecommendation.stance !== "Hold" &&
        Math.abs(topLineRecommendation.deltaPct ?? 0) >= 0.01
    );
}

function isStageReady(topLineRecommendation) {
    if (!topLineRecommendation) return false;
    return (
        topLineRecommendation.stance === "Hold" ||
        Math.abs(topLineRecommendation.deltaPct ?? 0) < 0.01
    );
}

function isCategoryCardApplied(card, appliedDecisions) {
    const hasGovernedDestination = card.pctDelta >= -0.0005 || card.receivers.length > 0;
    const isActionable =
        Math.abs(card.pctDelta) >= 0.0005 && card.stance !== "Hold" && hasGovernedDestination;
    const expectedNextPct = isActionable ? card.recommendedPct : card.currentPct;
    return isForecastRecommendationApplied(
        appliedDecisions,
        card.recommendationId,
        expectedNextPct
    );
}

function isSubCategoryCardApplied(card, appliedDecisions) {
    const isActionable = Math.abs(card.pctDelta) >= 0.0005 && card.stance !== "Hold";
    const expectedNextPct = isActionable ? card.recommendedPct : card.currentPct;
    return isForecastRecommendationApplied(
        appliedDecisions,
        card.recommendationId,
        expectedNextPct
    );
}

function countPendingSkuProposals(pendingSkuRebalanceGroups) {
    return (pendingSkuRebalanceGroups || []).reduce(
        (sum, group) => sum + (group.rows?.length ?? 0),
        0
    );
}

/**
 * Header tile counts aligned with AiRebalanceForecastPanel display rules:
 * - Queued: top-line revenue (before staging unlocks) + staged category shifts
 * - Pending: staged subcategory shifts + SKU rebalance proposal cards
 * - History: decision log entries
 */
export function countAiRebalanceGovernanceTiles({
    recommendations,
    appliedDecisions,
    pendingSkuRebalanceGroups,
    decisionLogs,
}) {
    const historyCount = (decisionLogs || []).length;
    if (!recommendations) {
        return { queuedCount: 0, pendingCount: 0, historyCount };
    }

    const { topLineRecommendation, categoryCards = [], subCategoryCards = [] } = recommendations;
    const stageReady = isStageReady(topLineRecommendation);
    const decisions = appliedDecisions ?? new Map();

    let queuedCount = 0;
    if (
        !stageReady &&
        isTopLineActionable(topLineRecommendation) &&
        !isForecastRecommendationApplied(
            decisions,
            topLineRecommendation.recommendationId,
            topLineRecommendation.recommendedTarget
        )
    ) {
        queuedCount += 1;
    }

    if (stageReady) {
        queuedCount += categoryCards.filter(
            (card) => !isCategoryCardApplied(card, decisions)
        ).length;
    }

    const pendingSubCategoryCount = stageReady
        ? subCategoryCards.filter((card) => !isSubCategoryCardApplied(card, decisions)).length
        : 0;
    const pendingSkuCount = countPendingSkuProposals(pendingSkuRebalanceGroups);
    const pendingCount = pendingSubCategoryCount + pendingSkuCount;

    return { queuedCount, pendingCount, historyCount };
}
